import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, Clock, MapPin, Search, Waves, Trophy, Zap, X } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { toast } from "sonner";

const SESSION_STYLES = {
  morning: {
    strip: "bg-[#00F0FF]",
    iconBg: "bg-[#00F0FF]/10 border-[#00F0FF]/25",
    iconColor: "text-[#00F0FF]",
    label: "text-[#00F0FF]",
    countBg: "bg-[#00F0FF]/8 border-[#00F0FF]/20 text-[#00F0FF]",
    Icon: Waves,
  },
  evening: {
    strip: "bg-[#2563EB]",
    iconBg: "bg-[#2563EB]/10 border-[#2563EB]/25",
    iconColor: "text-[#60A5FA]",
    label: "text-[#60A5FA]",
    countBg: "bg-[#2563EB]/8 border-[#2563EB]/20 text-[#60A5FA]",
    Icon: Trophy,
  },
  closing: {
    strip: "bg-[#7C3AED]",
    iconBg: "bg-[#7C3AED]/10 border-[#7C3AED]/25",
    iconColor: "text-[#A78BFA]",
    label: "text-[#A78BFA]",
    countBg: "bg-[#7C3AED]/8 border-[#7C3AED]/20 text-[#A78BFA]",
    Icon: Zap,
  },
};

function resolveStyle(sessionName) {
  const s = sessionName.toLowerCase();
  if (s.includes("closing")) return SESSION_STYLES.closing;
  if (s.includes("evening") || s.includes("final")) return SESSION_STYLES.evening;
  return SESSION_STYLES.morning;
}

/* ── Skeleton placeholders ─────────────────────────────────────────── */
function SkeletonCard() {
  return (
    <div className="relative rounded-xl border border-white/[0.06] bg-[#0A1128] p-5 overflow-hidden animate-pulse">
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-white/10 rounded-l-xl" />
      <div className="pl-1">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-9 h-9 rounded-lg bg-white/[0.06]" />
          <div className="flex-1">
            <div className="h-3 bg-white/[0.06] rounded w-24 mb-2" />
            <div className="h-4 bg-white/[0.06] rounded w-16" />
          </div>
        </div>
        <div className="h-px bg-white/[0.04] mb-3" />
        <div className="space-y-2.5">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-white/[0.06] shrink-0" />
              <div className="h-4 bg-white/[0.06] rounded flex-1" />
              <div className="h-5 bg-white/[0.06] rounded w-10 shrink-0" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Single event row inside a session card ────────────────────────── */
function EventRow({ ev, index, style }) {
  const isRelay = ev.category === "relay";
  return (
    <motion.div
      initial={{ opacity: 0, x: -6 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.035, duration: 0.22 }}
      className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-white/[0.04] transition-colors duration-150"
    >
      <span className={`w-2 h-2 rounded-full shrink-0 ${isRelay ? "bg-[#60A5FA]" : style.strip}`} />
      <span className="text-[#CBD5E1] text-sm font-medium flex-1 min-w-0 leading-snug">{ev.name}</span>
      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border shrink-0 ${isRelay
        ? "text-[#60A5FA] border-[#2563EB]/35 bg-[#1D4ED8]/10"
        : `${style.countBg} border`
        }`}>
        {isRelay ? "Relay" : "Ind"}
      </span>
    </motion.div>
  );
}

/* ── Session card ──────────────────────────────────────────────────── */
function SessionCard({ session, cardIndex }) {
  const style = resolveStyle(session.session);
  const { Icon } = style;

  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: cardIndex * 0.09, duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
      className="relative rounded-xl border border-white/[0.07] bg-[#0A1128] overflow-hidden"
    >
      {/* Accent strip */}
      <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${style.strip}`} />

      <div className="pl-5 pr-5 pt-5 pb-4">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-9 h-9 rounded-lg border flex items-center justify-center shrink-0 ${style.iconBg}`}>
              <Icon className={`w-4 h-4 ${style.iconColor}`} />
            </div>
            <div className="min-w-0">
              <div className={`text-[10px] font-bold uppercase tracking-[0.22em] ${style.label} truncate`}>
                {session.session}
              </div>
              <div className="text-white font-semibold text-sm mt-0.5 tabular-nums">{session.time}</div>
            </div>
          </div>
          <span className={`shrink-0 text-[10px] font-bold px-2.5 py-1 rounded-full border ${style.countBg}`}>
            {session.events.length} event{session.events.length !== 1 ? "s" : ""}
          </span>
        </div>

        <div className="h-px bg-white/[0.05] -mx-1 mb-1" />

        {/* Events */}
        <div className="-mx-2">
          {session.events.map((ev, i) => (
            <EventRow key={i} ev={ev} index={i} style={style} />
          ))}
        </div>
      </div>
    </motion.div>
  );
}

/* ── Day tab button ────────────────────────────────────────────────── */
function DayTab({ day, isSelected, isToday, totalEvents, onClick }) {
  return (
    <button
      onClick={onClick}
      aria-pressed={isSelected}
      className={`relative flex flex-col items-center min-w-[72px] px-4 py-3 rounded-xl border font-display font-bold text-sm transition-all duration-200 cursor-pointer touch-manipulation select-none ${isSelected
        ? "bg-[#00F0FF]/10 border-[#00F0FF]/60 text-[#00F0FF] shadow-[0_0_20px_rgba(0,240,255,0.15)]"
        : "bg-[#0A1128] border-white/[0.07] text-[#64748B] hover:border-white/[0.18] hover:text-[#94A3B8]"
        }`}
    >
      {isToday && (
        <span className="absolute -top-1.5 -right-1.5 w-3 h-3 rounded-full bg-[#00F0FF] pulse-ring" />
      )}
      <span className="text-[10px] tracking-[0.18em] uppercase font-semibold opacity-70 mb-0.5 tabular-nums">
        {day.date.slice(5).replace("-", "/")}
      </span>
      <span className="leading-none">{day.label}</span>
    </button>
  );
}

/* ── Main page ─────────────────────────────────────────────────────── */
export default function Schedule() {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(1);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  useEffect(() => {
    api.get("/schedule")
      .then(({ data }) => setSchedule(data))
      .catch(() => toast.error("Failed to load schedule"))
      .finally(() => setLoading(false));
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const todayDayNum = useMemo(() => {
    if (!schedule?.days) return null;
    const match = schedule.days.find(d => d.date === today);
    return match ? match.day : null;
  }, [schedule, today]);

  const currentDay = useMemo(() => {
    if (!schedule?.days) return null;
    return schedule.days.find(d => d.day === selectedDay);
  }, [schedule, selectedDay]);

  const filteredSessions = useMemo(() => {
    if (!currentDay) return [];
    const q = search.toLowerCase();
    return currentDay.sessions
      .map(session => ({
        ...session,
        events: session.events.filter(ev => {
          const matchesSearch = !q || ev.name.toLowerCase().includes(q);
          const matchesCategory = categoryFilter === "all" || ev.category === categoryFilter;
          return matchesSearch && matchesCategory;
        }),
      }))
      .filter(s => s.events.length > 0);
  }, [currentDay, search, categoryFilter]);

  const hasActiveFilter = search || categoryFilter !== "all";

  const totalEventsPerDay = useMemo(() => {
    if (!schedule?.days) return {};
    return Object.fromEntries(
      schedule.days.map(d => [
        d.day,
        d.sessions.reduce((sum, s) => sum + s.events.length, 0),
      ])
    );
  }, [schedule]);

  /* Loading skeleton */
  if (loading) {
    return (
      <div className="min-h-dvh bg-[#050914]">
        <Navbar />
        <div className="max-w-6xl mx-auto px-4 pt-32 pb-10">
          <div className="h-6 bg-white/[0.05] rounded w-48 mx-auto mb-4 animate-pulse" />
          <div className="h-12 bg-white/[0.05] rounded w-72 mx-auto mb-8 animate-pulse" />
          <div className="flex gap-2 mb-8">
            {[1, 2, 3, 4, 5, 6, 7].map(i => (
              <div key={i} className="h-16 w-20 bg-white/[0.05] rounded-xl animate-pulse" />
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        </div>
      </div>
    );
  }

  if (!schedule) return null;

  const totalEventCount = schedule.days.reduce(
    (sum, d) => sum + d.sessions.reduce((s2, s) => s2 + s.events.length, 0),
    0
  );

  return (
    <div className="min-h-dvh bg-[#050914]">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden pt-24 pb-8 border-b border-white/[0.06]">
        <div className="absolute inset-0 pool-tiles opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050914]/30 via-transparent to-[#050914]/60" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 text-center">
          <div className="eyebrow mb-3">AMSC INDIA 2026 · OFFICIAL PROGRAMME</div>
          <h1 className="font-display font-black text-4xl md:text-6xl leading-[0.9] tracking-tight mb-5">
            Competition <span className="shimmer-text">Schedule</span>
          </h1>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-sm text-[#64748B]">
            <span className="flex items-center gap-2">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#00F0FF] flex-shrink-0" />

                <a
                  href="https://www.google.com/maps/search/?api=1&query=Dolphin+Aquatics+Centre+for+Sports+Excellence+Bangalore+Karnataka+India"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#00F0FF] transition-all duration-300 hover:underline underline-offset-4"
                >
                  {schedule.venue}
                </a>
              </div>

            </span>
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#00F0FF]" />
              {schedule.championship_dates}
            </span>
          </div>
          {/* Stats strip */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 px-5 py-3 rounded-full bg-white/[0.04] border border-white/[0.08] text-xs text-[#64748B] mx-auto w-fit max-w-full">
            <span><span className="font-bold text-white tabular-nums">{schedule.days.length}</span> Days</span>
            <span className="w-px h-4 bg-white/10 hidden sm:block" />
            <span><span className="font-bold text-white tabular-nums">{totalEventCount}</span> Events</span>
            <span className="w-px h-4 bg-white/10 hidden sm:block" />
            <span><span className="font-bold text-white">Bengaluru</span>, India</span>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 lg:px-10 py-5">

        {/* Day picker */}
        <div className="relative mb-6">
          <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-4 bg-gradient-to-r from-[#050914]/60 to-transparent z-10" />
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-4 bg-gradient-to-l from-[#050914]/60 to-transparent z-10" />
          <div className="overflow-x-auto pb-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="flex gap-2 min-w-max px-2">
            {schedule.days.map(d => (
              <DayTab
                key={d.day}
                day={d}
                isSelected={d.day === selectedDay}
                isToday={d.day === todayDayNum}
                totalEvents={totalEventsPerDay[d.day] || 0}
                onClick={() => setSelectedDay(d.day)}
              />
            ))}
          </div>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {currentDay && (
            <motion.div
              key={currentDay.day}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              transition={{ duration: 0.18 }}
            >
              {/* Day header + filters */}
              <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                <div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <h2 className="font-display font-black text-2xl md:text-3xl text-white">
                      {currentDay.label}
                    </h2>
                    {currentDay.day === todayDayNum && (
                      <span className="px-2.5 py-1 rounded-full bg-[#00F0FF] text-[#050914] text-[10px] font-black tracking-[0.2em] uppercase">
                        Live Today
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-4 mt-1.5 text-xs text-[#475569]">
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 shrink-0" />
                      {new Date(currentDay.date + "T00:00:00").toLocaleDateString("en-US", {
                        weekday: "long", month: "long", day: "numeric", year: "numeric",
                      })}
                    </span>
                    <span className="flex items-center gap-1.5 text-[#00F0FF]/60">
                      <Clock className="w-3.5 h-3.5 shrink-0" />
                      Warm-up: {currentDay.warmup}
                    </span>
                  </div>
                </div>

                {/* Filters */}
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#475569]" />
                    <input
                      type="text"
                      placeholder="Search event…"
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      aria-label="Search events"
                      className="h-11 bg-[#0A1128] border border-white/[0.08] rounded-lg pl-8 pr-8 text-sm text-white placeholder:text-[#475569] focus:outline-none focus:border-[#00F0FF]/40 w-44 transition-colors"
                    />
                    {search && (
                      <button
                        onClick={() => setSearch("")}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#475569] hover:text-white transition-colors"
                        aria-label="Clear search"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>

                  {/* Category pills */}
                  <div className="flex items-center gap-1 bg-[#0A1128] border border-white/[0.08] rounded-lg p-1">
                    {["all", "individual", "relay"].map(cat => (
                      <button
                        key={cat}
                        onClick={() => setCategoryFilter(cat)}
                        className={`h-9 md:h-7 px-3 rounded-md text-[11px] font-semibold uppercase tracking-wide transition-all duration-150 cursor-pointer ${categoryFilter === cat
                          ? "bg-[#00F0FF]/12 text-[#00F0FF] border border-[#00F0FF]/25"
                          : "text-[#475569] hover:text-[#94A3B8]"
                          }`}
                      >
                        {cat === "all" ? "All" : cat === "individual" ? "Ind." : "Relay"}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Session cards */}
              {filteredSessions.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-4">
                  {filteredSessions.map((session, i) => (
                    <SessionCard key={session.session} session={session} cardIndex={i} />
                  ))}
                </div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-20 text-[#475569]"
                >
                  <Search className="w-8 h-8 mx-auto mb-3 opacity-25" />
                  <p className="font-display font-bold text-base mb-1 text-[#64748B]">No events match your filters</p>
                  <p className="text-sm text-[#475569] mb-4">Try adjusting your search or category</p>
                  {hasActiveFilter && (
                    <button
                      onClick={() => { setSearch(""); setCategoryFilter("all"); }}
                      className="text-[#00F0FF] text-sm hover:underline transition-opacity"
                    >
                      Clear all filters
                    </button>
                  )}
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Legend */}
        <div className="mt-10 pt-6 border-t border-white/[0.06]">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-[#475569]">
            <span className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-[#00F0FF]" />
              Individual event
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-[#60A5FA]" />
              Relay event
            </span>
            <span className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-[#00F0FF] pulse-ring" />
              Today
            </span>
            <span className="ml-auto text-[#3A4A6A] tabular-nums">All times IST (UTC+5:30)</span>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
